import {
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { RepeatByDateKind } from '@planning/app/api'
import { useTranslate } from '@tolgee/react'
import { FC } from 'react'
import { carrierVisitScheduleDefaultValues, ICarrierVisitSchedule } from './CarrierVisitSchedule'

interface ICarrierVisitScheduleTimingsProps {
  schedule: ICarrierVisitSchedule
  onChange: (newValue: ICarrierVisitSchedule) => void
}
export const CarrierVisitScheduleTimings: FC<ICarrierVisitScheduleTimingsProps> = ({
  schedule,
  onChange,
}) => {
  const { t } = useTranslate()

  return (
    <>
      {schedule.showTimingOptions > 0 && (
        <>
          <Grid container direction='row' columnSpacing={{ xs: 1 }} mt={'1rem'}>
            <Grid sx={{ display: 'flex', alignItems: 'center' }} size={4}>
              <Typography variant='subtitle2'>{t('repeatsEvery', 'Repeats every')}</Typography>
            </Grid>
            <Grid size={3}>
              <TextField
                fullWidth
                type='number'
                defaultValue={carrierVisitScheduleDefaultValues.repeatEvery}
                name='repeatEvery'
                inputProps={{
                  min: 1,
                  max: 12,
                  step: '1',
                }}
                onChange={event =>
                  onChange({
                    ...schedule,
                    repeatEvery: +event.target.value,
                  })
                }
                onWheel={event => event.target instanceof HTMLElement && event.target.blur()}
                sx={{ margin: 0 }}
              />
            </Grid>
            <Grid size={5}>
              <Select
                fullWidth
                defaultValue={carrierVisitScheduleDefaultValues.repeatBy}
                required
                onChange={event =>
                  onChange({
                    ...schedule,
                    repeatBy: event.target.value as RepeatByDateKind,
                  })
                }
              >
                <MenuItem value={RepeatByDateKind.Day}>{t('days', 'Days')}</MenuItem>
                <MenuItem value={RepeatByDateKind.Week}>{t('weeks', 'Weeks')}</MenuItem>
                <MenuItem value={RepeatByDateKind.Month}>{t('months', 'Months')}</MenuItem>
              </Select>
            </Grid>
          </Grid>
          <RadioGroup
            value={schedule.selectedRadioOption}
            onChange={event =>
              onChange({
                ...schedule,
                selectedRadioOption: event.target.value,
              })
            }
          >
            <Grid container direction='row' columnSpacing={{ xs: 1 }}>
              <Grid size={12}>
                <Typography variant='subtitle2'>{t('ends', 'Ends')}</Typography>
              </Grid>

              <Grid sx={{ display: 'flex', alignItems: 'center' }} size={2}>
                <FormControlLabel value='on' control={<Radio />} label='On' />
              </Grid>
              <Grid size={10}>
                <DatePicker
                  format='DD/MM/YYYY'
                  value={schedule.endsOn}
                  disabled={schedule.selectedRadioOption !== 'on'}
                  onChange={newValue =>
                    onChange({
                      ...schedule,
                      endsOn: newValue ?? undefined,
                    })
                  }
                  slotProps={{
                    textField: {
                      variant: 'outlined',
                      helperText:
                        schedule.selectedRadioOption === 'on' && !schedule.endsOn
                          ? t('fieldIsRequired', 'Field is required')
                          : '',
                      error: schedule.selectedRadioOption === 'on' && !schedule.endsOn,
                      required: true,
                      sx: { width: '100%' },
                    },
                  }}
                />
              </Grid>

              <Grid mt={'1rem'} sx={{ display: 'flex', alignItems: 'center' }} size={2}>
                <FormControlLabel value='after' control={<Radio />} label='After' />
              </Grid>
              <Grid mt={'1rem'} size={10}>
                <TextField
                  type='number'
                  name='endsAfter'
                  fullWidth
                  disabled={schedule.selectedRadioOption !== 'after'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        {t('occurences', 'Occurences')}
                      </InputAdornment>
                    ),
                  }}
                  defaultValue={carrierVisitScheduleDefaultValues.endsAfter}
                  inputProps={{
                    min: 1,
                    step: '1',
                  }}
                  onChange={event =>
                    onChange({
                      ...schedule,
                      endsAfter: +event.target.value,
                    })
                  }
                  variant={'outlined'}
                  helperText={
                    schedule.selectedRadioOption === 'after' && !schedule.endsAfter
                      ? t('fieldIsRequired', 'Field is required')
                      : ''
                  }
                  error={schedule.selectedRadioOption === 'after' && !schedule.endsAfter}
                  required
                  sx={{ width: '100%' }}
                  onWheel={event => event.target instanceof HTMLElement && event.target.blur()}
                />
              </Grid>
            </Grid>
          </RadioGroup>
        </>
      )}
    </>
  )
}
